import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEllipsisVertical, 
  faTrash, 
  faUserPlus,
  faQrcode,
  faRefresh,
  faHome,
  faSignOutAlt,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../../lib/supabase';
import useAuth from '../../hooks/useAuth';
import ConnectQrModal from '../../components/ConnectionTable/ConnectQrModal';
import EnterpriseInstanceTable from './components/EnterpriseInstanceTable';
import CreateInstanceModal from './components/CreateInstanceModal';
import './enterprise.css';
import { getInstanceStatus, updateInstancePhoneNumber } from '../../services/api';
import i18n from '../../i18n';
import { apiRequest } from '../../config/api';
import { FaTriangleExclamation } from 'react-icons/fa6';

const Enterprise = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, signed, loading: authLoading, signout } = useAuth();
  const [enterprise, setEnterprise] = useState(null);
  const [users, setUsers] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userInstances, setUserInstances] = useState([]);
  const [recreateLoading, setRecreateLoading] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [recreatingInstanceId, setRecreatingInstanceId] = useState(null);
  const [showRecreateConfirm, setShowRecreateConfirm] = useState(false);
  const [instanceToRecreate, setInstanceToRecreate] = useState(null);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectInstance, setConnectInstance] = useState(null);
  const [showCreateInstance, setShowCreateInstance] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    phone_number: '',
    password: '',
    role: 'Normal'
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [instanceStatuses, setInstanceStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRecreateModal, setShowRecreateModal] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [limitInputs, setLimitInputs] = useState({});
  const [limitInputErrors, setLimitInputErrors] = useState({});

  // Estados para paginação de instâncias
  const [instancesPage, setInstancesPage] = useState(1);
  const [instancesPerPage, setInstancesPerPage] = useState(10);
  
  // Estados para paginação de usuários
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);

  // Calcular instâncias paginadas
  const totalInstances = instances.length;
  const totalInstancesPages = Math.ceil(totalInstances / instancesPerPage);
  const startInstanceIndex = (instancesPage - 1) * instancesPerPage;
  const endInstanceIndex = startInstanceIndex + instancesPerPage;
  const paginatedInstances = instances.slice(startInstanceIndex, endInstanceIndex);

  // Calcular usuários paginados
  const totalUsers = users.length;
  const totalUsersPages = Math.ceil(totalUsers / usersPerPage);
  const startUserIndex = (usersPage - 1) * usersPerPage;
  const endUserIndex = startUserIndex + usersPerPage;
  const paginatedUsers = users.slice(startUserIndex, endUserIndex);

  useEffect(() => {
    // Só executa se tiver o ID do enterprise e o usuário estiver carregado
    if (!id || authLoading) {
      if (!id) {
        setLoading(false);
        setError(t('enterprise.errors.missingId'));
      }
      return;
    }

    const fetchEnterpriseData = async () => {
      try {
        setLoading(true);
        setError(''); // Limpa erros anteriores
        setSuccess(''); // Limpa mensagens de sucesso anteriores
        
        // Verifica se o usuário está autenticado
        if (!signed || !user) {
          return;
        }
        
        // Reset de estados
        setEnterprise(null);
        setUsers([]);
        setInstances([]);
        setCurrentUser(null);

        // Usa dados do contexto de autenticação primeiro
        let userData = null;
        if (user && user.enterprise_id && user.enterprise_role) {
          userData = user;
          
          // Verifica se o usuário tem acesso a este enterprise
          if (user.enterprise_id !== id && user.enterprise_role === 'Normal') {
            throw new Error(t('enterprise.errors.accessDenied', { id }));
          }
          
          if (!['Admin', 'Normal'].includes(user.enterprise_role)) {
            throw new Error(t('enterprise.errors.invalidRole'));
          }
          
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            try {
              const fetchedUserData = await apiRequest(`/auth/user/${session.user.id}`);

              if (fetchedUserData) {
                userData = fetchedUserData;

                // Verifica se o usuário tem acesso a este enterprise
                if (fetchedUserData.enterprise_id !== id && fetchedUserData.enterprise_role === 'Normal') {
                  throw new Error(t('enterprise.errors.accessDenied', { id }));
                }

                if (!fetchedUserData.enterprise_role || !['Admin', 'Normal'].includes(fetchedUserData.enterprise_role)) {
                  throw new Error(t('enterprise.errors.invalidRole'));
                }
              } else {
                throw new Error('Não foi possível carregar dados do usuário atual');
              }
            } catch (error) {
              throw new Error('Não foi possível carregar dados do usuário atual');
            }
          } else {
            throw new Error('Usuário não autenticado');
          }
        }

        setCurrentUser(userData);

        // Busca dados da enterprise
        const enterpriseData = await apiRequest(`/enterprises/${id}`);

        if (!enterpriseData) {
          throw new Error(t('enterprise.errors.notFound'));
        }

        setEnterprise(enterpriseData);

        // Busca todos os usuários do enterprise (incluindo Admins para uma visão completa)
        const allUsersData = await apiRequest(`/enterprises/${id}/users`);

        // Separa usuários Normal dos Admins
        const normalUsers = (allUsersData || []).filter(u => u.enterprise_role === 'Normal');
        const allUsers = allUsersData || [];

        setUsers(normalUsers);

        // Buscar instâncias baseado no role do usuário

        
        let userIds = [];
        let usersForInstances = [];
        
        if (userData.enterprise_role === 'Admin') {
          // Admin vê todas as instâncias do enterprise
          userIds = allUsers.map(u => u.id);
          usersForInstances = allUsers;

        } else {
          // Normal vê apenas suas próprias instâncias
          userIds = [userData.id];
          usersForInstances = [userData];

        }
        
        let finalInstances = [];

        if (userIds.length > 0) {
          // Buscar instâncias via API
          const instancesData = await apiRequest(`/enterprises/${id}/instances`);

          // Filtrar instâncias baseado no role do usuário
          let filteredInstances = instancesData || [];
          if (userData.enterprise_role !== 'Admin') {
            // Normal vê apenas suas próprias instâncias
            filteredInstances = filteredInstances.filter(inst => inst.user_id === userData.id);
          }

          // Mapear instâncias com dados do usuário
          finalInstances = filteredInstances.map(instance => ({
            ...instance,
            Users: usersForInstances.find(u => u.id === instance.user_id) || {
              name: userData.name || 'Usuário desconhecido',
              email: userData.email || 'Email desconhecido',
              enterprise_role: userData.enterprise_role || 'Normal'
            }
          }));
        }
        
        setInstances(finalInstances);
        
        // Buscar status das instâncias
        if (finalInstances.length > 0) {
          await fetchAllInstanceStatuses(finalInstances);
        }

      } catch (err) {
        console.error('❌ Enterprise: Erro ao buscar dados do enterprise:', err);
        
        // Se for erro de acesso negado, redireciona para home
        if (err.message.includes('accessDenied') || err.message.includes('Access denied')) {
          navigate('/home');
          return;
        }
        
        setError(err.message);
        setEnterprise(null);
        setUsers([]);
        setInstances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEnterpriseData();
  }, [id, authLoading]); // Adicionado authLoading para aguardar carregamento do usuário

  // Função para buscar status de todas as instâncias
  const fetchAllInstanceStatuses = async (instancesList) => {
    setStatusLoading(true);
    const statuses = {};
    let hasPhoneNumberUpdate = false;
    
    await Promise.all(
      (instancesList || []).map(async (instance) => {
        try {
          const statusRes = await getInstanceStatus(
            instance.instanceName,
            instance.server_id
          );
          statuses[instance.id] = statusRes?.status || 'UNKNOWN';
          
          // Se a instância está conectada e não tem número de telefone, atualiza
          if (statusRes?.status === 'WORKING' && statusRes?.me?.id && !instance.phone_number) {

            // Extrai apenas o número do telefone, removendo @c.us
            const phoneNumber = statusRes.me.id.replace('@c.us', '');
            try {
              await updateInstancePhoneNumber(instance.id, phoneNumber);
              hasPhoneNumberUpdate = true;

            } catch (phoneError) {
              console.error('❌ Erro ao atualizar número:', phoneError);
            }
          }
        } catch {
          statuses[instance.id] = 'UNKNOWN';
        }
      })
    );
    
    setInstanceStatuses(statuses);
    setStatusLoading(false);
    
    // Se houve atualização de número de telefone, recarrega as instâncias
    if (hasPhoneNumberUpdate) {
      
      setTimeout(() => {
        // Recarrega após um pequeno delay para garantir que a atualização foi salva
        window.location.reload();
      }, 1000);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setCreateUserLoading(true);

      if (!newUser.password || newUser.password.length < 6) {
        setError(t('enterprise.users.form.passwordMinLength'));
        setCreateUserLoading(false);
        return;
      }



      // Criar usuário usando a API do backend (evita login automático)
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3300/api'}/enterprise/${enterprise.id}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            email: newUser.email,
            password: newUser.password,
            name: newUser.name,
            phone_number: newUser.phone_number,
            role: 'Normal'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || 'Erro ao criar usuário via API');
        }

        const result = await response.json();


      } catch (apiError) {
        console.error('❌ Erro na API:', apiError);
        
        // Fallback: mostrar erro mais específico
        console.error('❌ API não está funcionando. Verifique se o servidor está rodando.');
        throw new Error('Servidor não está respondendo. Verifique se o middleware está rodando em http://localhost:3300');
      }

      // Recarregar lista de usuários
      const updatedUsers = await apiRequest(`/enterprises/${enterprise.id}/users`);
      const normalUsers = (updatedUsers || []).filter(u => u.enterprise_role === 'Normal');

      setUsers(normalUsers);
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', phone_number: '', password: '', role: 'Normal' });
      setSuccess(t('enterprise.users.form.success'));
      
      setTimeout(() => {
        setSuccess('');
      }, 4000);

    } catch (error) {
      console.error('❌ Erro ao criar usuário:', error);
      setError('Erro ao criar usuário: ' + error.message);
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('enterprise.users.confirmRemove'))) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3300/api'}/enterprise/${enterprise.id}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Erro ao remover usuário');
      }

      const updatedUsers = await apiRequest(`/enterprises/${enterprise.id}/users`);
      const normalUsers = (updatedUsers || []).filter(u => u.enterprise_role === 'Normal');

      setUsers(normalUsers);
      setSuccess(t('enterprise.users.removedSuccess'));
      
      setTimeout(() => {
        setSuccess('');
      }, 4000);

    } catch (error) {
      console.error('Erro ao remover usuário:', error);
      setError('Erro ao remover usuário: ' + error.message);
    }
  };

  const handleShowUserDetails = async (user) => {
    setSelectedUser(user);

    const userInstancesData = await apiRequest(`/instances/user/${user.id}`);

    setUserInstances(userInstancesData || []);
    setShowUserDetails(true);
  };

  const handleGenerateQRCode = async (instance) => {
    setConnectInstance({
      id: instance.id,
      name: instance.name || instance.instanceName,
      instance: instance.instanceName,
      instanceName: instance.instanceName,
      rawData: {
        server_id: instance.server_id
      }
    });
    setConnectModalOpen(true);
  };

  const handleCloseConnectModal = () => {
    setConnectModalOpen(false);
    setConnectInstance(null);
    // Recarrega as instâncias quando o modal é fechado
    setTimeout(() => {
      reloadInstancesTable();
    }, 1000);
  };

  const handleRecreateClick = (instance) => {
    setInstanceToRecreate(instance);
    setShowRecreateConfirm(true);
  };

  const handleRecreateInstance = (instance) => {
    setInstanceToRecreate(instance);
    setShowRecreateModal(true);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await signout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      setError('Erro ao fazer logout');
    } finally {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setRefreshingStatus(true);
      setError('');

      const { getInstanceStatus } = await import('../../services/api');

      const updatedInstances = await Promise.all(
        instances.map(async (instance) => {
          try {
            const data = await getInstanceStatus(instance.instanceName, instance.server_id);
            return {
              ...instance,
              status: data.status || data.instance?.status || instance.status
            };
          } catch (error) {
            console.error(`Erro ao atualizar status da instância ${instance.instanceName}:`, error);
            return instance;
          }
        })
      );

      setInstances(updatedInstances);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      setError('Erro ao atualizar status das instâncias');
    } finally {
      setRefreshingStatus(false);
    }
  };

  const handleUpdateUserLimit = async (userId, newLimit) => {
    try {
      setError('');

      // Busca dados do usuário para verificar se é Admin
      const userData = await apiRequest(`/auth/user/${userId}`);

      // Admin não tem limite individual
      if (userData.enterprise_role === 'Admin') {
        setError(t('enterprise.errors.adminNoIndividualLimit'));
        return;
      }

      // Verifica se o total de instâncias alocadas não excede o limite da enterprise
      const totalAllocated = users.reduce((total, u) => {
        if (u.id === userId) return total + newLimit;
        return total + (u.max_instances || 3);
      }, 0);

      if (totalAllocated > (enterprise.max_instances || 300)) {
        setError(`Total de instâncias alocadas (${totalAllocated}) excede o limite da enterprise (${enterprise.max_instances || 300})`);
        return;
      }

      await apiRequest(`/users/${userId}/instance-limit`, {
        method: 'PATCH',
        body: JSON.stringify({ max_instances: newLimit })
      });

      // Atualiza a lista de usuários
      const updatedUsers = await apiRequest(`/enterprises/${id}/users`);

      const normalUsers = (updatedUsers || []).filter(u => u.enterprise_role === 'Normal');
      setUsers(normalUsers);

      setSuccess(t('enterprise.users.limitUpdatedSuccess'));
      setTimeout(() => setSuccess(''), 4000);

    } catch (error) {
      console.error('Erro ao atualizar limite:', error);
      setError('Erro ao atualizar limite: ' + error.message);
    }
  };

  const handleLimitInputChange = (userId, value) => {
    const numValue = parseInt(value) || 0;
    const currentUser = users.find(u => u.id === userId);
    const currentLimit = currentUser?.max_instances || 3;
    
    // Atualiza o input
    setLimitInputs(prev => ({
      ...prev,
      [userId]: value
    }));

    // Calcula o total alocado se este valor fosse aplicado
    const totalAllocated = users.reduce((total, u) => {
      if (u.id === userId) return total + numValue;
      return total + (u.max_instances || 3);
    }, 0);

    // Valida o valor
    let error = '';
    if (numValue < 1) {
      error = 'Mínimo 1 instância';
    } else if (totalAllocated > (enterprise?.max_instances || 300)) {
      error = `Total excederia o limite da enterprise (${enterprise?.max_instances || 300})`;
    } else if (numValue === currentLimit) {
      // Se não mudou, limpa o input
      setLimitInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[userId];
        return newInputs;
      });
    }

    setLimitInputErrors(prev => ({
      ...prev,
      [userId]: error
    }));
  };

  const handleLimitInputBlur = async (userId) => {
    const inputValue = limitInputs[userId];
    if (!inputValue) return;

    const numValue = parseInt(inputValue);
    if (isNaN(numValue) || numValue < 1) {
      // Limpa o input se valor inválido
      setLimitInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[userId];
        return newInputs;
      });
      setLimitInputErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[userId];
        return newErrors;
      });
      return;
    }

    // Aplica a mudança
    await handleUpdateUserLimit(userId, numValue);
    
    // Limpa o input após aplicar
    setLimitInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[userId];
      return newInputs;
    });
    setLimitInputErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[userId];
      return newErrors;
    });
  };

  const handleLimitInputKeyPress = (e, userId) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Calcula o total de instâncias alocadas
  const calculateTotalAllocated = () => {
    return users.reduce((total, user) => total + (user.max_instances || 3), 0);
  };

  // Calcula o limite disponível
  const calculateAvailableLimit = () => {
    const totalAllocated = calculateTotalAllocated();
    return Math.max(0, (enterprise?.max_instances || 300) - totalAllocated);
  };

  const handleCreateInstanceForUser = async (user) => {
    try {
      setError('');
      
      if (user.instances_total >= user.max_instances) {
        setError(t('enterprise.errors.userLimitReached', { userName: user.name, limit: user.max_instances }));
        return;
      }

      // Verifica limite da enterprise
      if (instances.length >= (enterprise.current_instances || enterprise.max_instances || 10)) {
        setError(t('enterprise.errors.enterpriseLimitReached', { max: enterprise.max_instances || 10 }));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3300/api'}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          instanceName: `Instância ${user.name}`,
          name: `Instância ${user.name}`,
          server_id: 1,
          user_id: user.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Erro ao criar instância');
      }

      const result = await response.json();

      // O middleware já salva a instância no banco — não inserir novamente aqui
      const instanceNameFromApi = result.instanceName || `Instância ${user.name}`;

      

      // Atualiza contador da enterprise
      await supabase
        .from('Enterprises')
        .update({ current_instances: (enterprise.current_instances || 0) + 1 })
        .eq('id', id);

      // instances_total é o limite do usuário — não deve ser sobrescrito com a contagem

      

      // Reinicia a instância para gerar QR
      try {
        const restartResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3300/api'}/sessions/${instanceNameFromApi}/restart`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            instanceName: instanceNameFromApi,
            server_id: 1
          })
        });

        if (restartResponse.ok) {

        } else {

        }
      } catch (restartError) {

      }

      // Recarrega instâncias de forma mais eficiente
      const { data: allUsersForInstances } = await supabase
        .from('Users')
        .select('id, name, email, enterprise_role')
        .eq('enterprise_id', id);

      const userIdsForInstances = allUsersForInstances?.map(u => u.id) || [];
      
      if (userIdsForInstances.length > 0) {
        const { data: updatedInstances } = await supabase
          .from('Instances')
          .select('*')
          .in('user_id', userIdsForInstances);

        // Enriquecer instâncias com dados dos usuários
        const enrichedInstances = (updatedInstances || []).map(instance => ({
          ...instance,
          Users: allUsersForInstances?.find(u => u.id === instance.user_id) || null
        }));

        setInstances(enrichedInstances);
      } else {
        setInstances([]);
      }

      const { data: updatedUsers } = await supabase
        .from('Users')
        .select('id, name, email, enterprise_role, created_at, instances_total, max_instances')
        .eq('enterprise_id', id);

      const normalUsers = (updatedUsers || []).filter(u => u.enterprise_role === 'Normal');
      setUsers(normalUsers);
              setSuccess(t('enterprise.instances.createdForUser', { userName: user.name }));
      
      setTimeout(() => {
        setSuccess('');
      }, 4000);

      // Abre o modal do QR após um pequeno delay
      setTimeout(() => {
        setConnectInstance({
          id: result.id || Date.now(),
          name: `Instância ${user.name}`,
          instance: instanceNameFromApi,
          instanceName: instanceNameFromApi,
          rawData: {
            server_id: result.server_id || 1
          }
        });
        setConnectModalOpen(true);
      }, 1000);

    } catch (error) {
      console.error('Erro ao criar instância para usuário:', error);
      setError('Erro ao criar instância: ' + error.message);
    }
  };
  
  if (authLoading) {
    return (
      <div className="enterprise-container">
        <div className="enterprise-loading">
          <div className="loading-spinner"></div>
          <p>Carregando autenticação...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="enterprise-container">
        <div className="enterprise-loading">
          <div className="loading-spinner"></div>
          <p>{t('enterprise.loading')}</p>
        </div>
      </div>
    );
  }

  if (!signed) {
    return (
      <div className="enterprise-container">
        <div className="enterprise-error">
          <p>Usuário não autenticado</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enterprise-container">
        <div className="enterprise-error">
          <p>{t('enterprise.error')}: {error}</p>
        </div>
      </div>
    );
  }

  if (!enterprise) {
    return (
      <div className="enterprise-container">
        <div className="enterprise-error">
          <p>{t('enterprise.errors.notFound')}</p>
        </div>
      </div>
    );
  }



  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  const handleDeleteInstance = (instance) => {
    setInstanceToDelete(instance);
    setShowDeleteModal(true);
  };

  const confirmDeleteInstance = async () => {
    if (!instanceToDelete) return;

    try {
      setError('');
      setShowDeleteModal(false);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3300/api'}/instances/${instanceToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instanceName: instanceToDelete.instanceName,
          server_id: instanceToDelete.server_id || 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Erro ao deletar instância');
      }



      // Remove do Supabase também
      const { error: supabaseError } = await supabase
        .from('Instances')
        .delete()
        .eq('id', instanceToDelete.id);

      if (supabaseError) {
        console.error('❌ Erro ao deletar do Supabase:', supabaseError);
        throw new Error('Instância deletada da API mas falhou ao remover do banco: ' + supabaseError.message);
      }



      // Atualiza contador da enterprise
      await supabase
        .from('Enterprises')
        .update({ current_instances: Math.max(0, (enterprise.current_instances || 0) - 1) })
        .eq('id', id);

      // instances_total é o limite do usuário — não deve ser sobrescrito com a contagem

      // Recarrega todas as instâncias da tabela
      await reloadInstancesTable();

      setSuccess(t('enterprise.instances.deletedSuccess', { 
        instanceName: instanceToDelete.name || instanceToDelete.instanceName 
      }));
      
      setTimeout(() => {
        setSuccess('');
      }, 4000);
      
      setInstanceToDelete(null);

    } catch (error) {
      console.error('Erro ao deletar instância:', error);
      setError('Erro ao deletar instância: ' + error.message);
      setInstanceToDelete(null);
    }
  };

  // Função para recarregar a tabela de instâncias
  const reloadInstancesTable = async () => {
    try {

      
      let userIds = [];
      let allUsersData = [];
      
      if (currentUser?.enterprise_role === 'Admin') {
        const { data: users } = await supabase
          .from('Users')
          .select('id, name, email, enterprise_role')
          .eq('enterprise_id', id);
        
        allUsersData = users || [];
        userIds = allUsersData.map(u => u.id);
      } else {
        userIds = [currentUser?.id].filter(Boolean);
        allUsersData = [currentUser];
      }
      
      let finalInstances = [];
      
      if (userIds.length > 0) {
        const { data: instancesData } = await supabase
          .from('Instances')
          .select(`
            *,
            Servers(host)
          `)
          .in('user_id', userIds)
          .order('created_at', { ascending: false });
        
        finalInstances = (instancesData || []).map(instance => ({
          ...instance,
          Users: allUsersData.find(u => u.id === instance.user_id) || {
            name: currentUser?.name || 'Usuário desconhecido',
            email: currentUser?.email || 'Email desconhecido',
            enterprise_role: currentUser?.enterprise_role || 'Normal'
          }
        }));
      }
      
      setInstances(finalInstances);
      
      // Atualiza contadores de usuários também
      const { data: updatedUsers } = await supabase
        .from('Users')
        .select('id, name, email, enterprise_role, created_at, instances_total, max_instances')
        .eq('enterprise_id', id);

      const normalUsers = (updatedUsers || []).filter(u => u.enterprise_role === 'Normal');
      setUsers(normalUsers);
      
      
    } catch (error) {
      console.error('❌ Erro ao recarregar tabela de instâncias:', error);
    }
  };

  const confirmRecreateInstance = async () => {
    if (!instanceToRecreate) return;

    try {
      setRecreateLoading(true);
      setRecreatingInstanceId(instanceToRecreate.id);
      setError('');
      setShowRecreateModal(false);

      const { restartInstance } = await import('../../services/api');
      
      await restartInstance(instanceToRecreate.instanceName, instanceToRecreate.server_id);

      setError('');
      setSuccess(t('enterprise.instances.restartedSuccess', { 
        instanceName: instanceToRecreate.name || instanceToRecreate.instanceName 
      }));
      
      setTimeout(() => {
        setSuccess('');
      }, 4000);

    } catch (error) {
      console.error('Erro ao reiniciar instância:', error);
      setError('Erro ao reiniciar instância: ' + error.message);
    } finally {
      setRecreateLoading(false);
      setRecreatingInstanceId(null);
      setInstanceToRecreate(null);
    }
  };

  return (
    <div className="enterprise-container">
      {/* Header com Logout e Idioma */}
      <div className="enterprise-top-header">
        <div className="enterprise-logo">
          <h2>{t('enterprise.title')}</h2>
        </div>
        <div className="enterprise-controls">
          {/* Select de Idioma */}
          <div className="language-select-container">
            <select 
              className="language-select"
              value={i18n.language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              title={t('header.selectLanguage')}
            >
              <option value="pt">Português</option>
              <option value="en">English</option>
            </select>
          </div>
          
          {/* Botão de Logout */}
          <button 
            className="logout-btn"
            onClick={() => setShowLogoutModal(true)}
            title={t('header.logout')}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            {t('header.logout')}
          </button>
        </div>
      </div>

      <div className="enterprise-main">
        <div className="enterprise-header">
          <div className="header-top">
            <h1>{enterprise.name}</h1>
            {currentUser?.enterprise_role === 'Admin' && (
              <button
                className="home-btn"
                onClick={() => navigate('/home')}
                title={t('enterprise.common.backToHome')}
              >
                <FontAwesomeIcon icon={faHome} />
                {t('enterprise.backToHome')}
              </button>
            )}
          </div>
          {/* Só mostra os cards de stats para Admin */}
          {currentUser?.enterprise_role === 'Admin' && (
            <div className="enterprise-stats">
              <div className="stat">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faUserPlus} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">{t('enterprise.stats.users')}</span>
                  <span className="stat-value">{users.length}</span>
                  <span className="stat-subtitle">{t('enterprise.stats.totalUsers')}</span>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faQrcode} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">{t('enterprise.stats.instances')}</span>
                  <span className="stat-value">
                    {instances.length} / {enterprise?.max_instances || 10}
                  </span>
                  <span className="stat-subtitle">
                    {t('enterprise.stats.instancesUsage')}
                  </span>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faRefresh} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">{t('enterprise.stats.activeInstances')}</span>
                  <span className="stat-value">
                    {Object.values(instanceStatuses).filter(status => status === 'WORKING').length}
                  </span>
                  <span className="stat-subtitle">
                    {t('enterprise.stats.onlineNow')}
                  </span>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faPlus} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">{t('enterprise.stats.available')}</span>
                  <span className="stat-value">
                    {Math.max(0, (enterprise?.max_instances || 10) - instances.length)}
                  </span>
                  <span className="stat-subtitle">
                    {t('enterprise.stats.remainingSlots')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Só mostra a seção de usuários para Admin */}
        {currentUser?.enterprise_role === 'Admin' && (
          <div className="admin-actions">
            <button
              className="create-user-btn"
              onClick={() => setShowCreateUser(true)}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              {t('enterprise.users.createUser')}
            </button>
          </div>
        )}

        {error && (
          <div className="error-message">
            <strong>❌ Erro:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="success-message" style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            color: '#10b981',
            padding: '1.25rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            fontWeight: '500'
          }}>
            <strong>✅ Sucesso:</strong> {success}
          </div>
        )}

        {/* Seção de Instâncias (movida para antes dos usuários) */}
        <EnterpriseInstanceTable
          instances={paginatedInstances}
          allInstances={instances}
          instanceStatuses={instanceStatuses}
          statusLoading={statusLoading}
          // Paginação
          currentPage={instancesPage}
          totalPages={totalInstancesPages}
          itemsPerPage={instancesPerPage}
          totalItems={totalInstances}
          onPageChange={setInstancesPage}
          onItemsPerPageChange={(newPerPage) => {
            setInstancesPerPage(newPerPage);
            setInstancesPage(1); // Reset para primeira página
          }}
          onInstancesUpdate={async () => {
            // Recarregar instâncias e status da mesma forma que o useEffect principal
            try {
              let userIds = [];
              let allUsersData = [];
              
              if (currentUser?.enterprise_role === 'Admin') {
                const { data: users } = await supabase
                  .from('Users')
                  .select('id, name, email, enterprise_role')
                  .eq('enterprise_id', id);
                
                allUsersData = users || [];
                userIds = allUsersData.map(u => u.id);
              } else {
                userIds = [currentUser?.id].filter(Boolean);
                allUsersData = [currentUser];
              }
              
              let finalInstances = [];
              
              if (userIds.length > 0) {
                const { data: instancesData } = await supabase
                  .from('Instances')
                  .select(`
                    *,
                    Servers(host)
                  `)
                  .in('user_id', userIds)
                  .order('created_at', { ascending: false });
                
                finalInstances = (instancesData || []).map(instance => ({
                  ...instance,
                  Users: allUsersData.find(u => u.id === instance.user_id) || {
                    name: currentUser?.name || 'Usuário desconhecido',
                    email: currentUser?.email || 'Email desconhecido',
                    enterprise_role: currentUser?.enterprise_role || 'Normal'
                  }
                }));
                
              }
              
              setInstances(finalInstances);
              
              if (finalInstances.length > 0) {
                await fetchAllInstanceStatuses(finalInstances);
              }
              
            } catch (error) {
              console.error('❌ Erro ao recarregar instâncias:', error);
            }
          }}
          onCreateInstance={() => setShowCreateInstance(true)}
          onDeleteInstance={handleDeleteInstance}
          onRecreateInstance={handleRecreateInstance}
          currentUser={currentUser}
          enterprise={enterprise}
        />

        {/* Só mostra a seção de usuários para Admin */}
        {currentUser?.enterprise_role === 'Admin' && (
          <section className="users-section">
            <div className="section-header-with-pagination">
                          <h2>
              <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: '0.5rem' }} />
              {t('enterprise.users.title')}
            </h2>
                          <div className="pagination-info">
              <span>
                {t('enterprise.users.pagination.showing', { 
                  start: totalUsers > 0 ? startUserIndex + 1 : 0,
                  end: Math.min(endUserIndex, totalUsers),
                  total: totalUsers
                })}
              </span>
            </div>
            </div>
            
            {/* Informações de limite da enterprise */}
            <div className="enterprise-limit-info">
              <div className="limit-summary">
                <span className="limit-label">Total Alocado:</span>
                <span className="limit-value allocated">{calculateTotalAllocated()}</span>
                <span className="limit-separator">/</span>
                <span className="limit-value total">{enterprise?.max_instances || 300}</span>
                <span className="limit-label">instâncias</span>
              </div>
              <div className="limit-available">
                <span className="available-label">Disponível:</span>
                <span className="available-value">{calculateAvailableLimit()}</span>
                <span className="available-label">instâncias</span>
              </div>
            </div>
            
            {/* Card do próprio Admin */}
            <div className="admin-card-section">
              <h3 style={{ color: '#f8fafc', marginBottom: '1rem', fontSize: '1.25rem' }}>
                <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: '0.5rem', color: '#ef4444' }} />
                {t('enterprise.users.myAccount')}
              </h3>
              <div className="user-card admin-card">
                <div className="user-info">
                  <h3>{currentUser.name}</h3>
                  <p>{currentUser.email}</p>
                  <span className="role-badge admin">{t('enterprise.common.admin')}</span>
                </div>
                <div className="user-stats">
                  <span>{t('enterprise.users.instances')}: {instances.filter(i => i.user_id === currentUser.id).length}</span>
                  <div className="admin-info">
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                      {t('enterprise.users.noIndividualLimit', { 
                        current: instances.length, 
                        max: enterprise.max_instances || 300 
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="users-grid">
              {paginatedUsers.map(user => (
                <div key={user.id} className="user-card">
                  <div className="user-info">
                    <h3>{user.name}</h3>
                    <p>{user.email}</p>
                    <span className={`role-badge ${user.enterprise_role?.toLowerCase()}`}>{user.enterprise_role || 'Normal'}</span>
                  </div>
                  <div className="user-stats">
                    <span>{t('enterprise.users.instances')}: {user.instances_total || 0} / {user.max_instances || 3}</span>
                    {currentUser?.enterprise_role === 'Admin' && (
                      <div className="limit-controls">
                        <button
                          className="limit-btn decrease"
                          onClick={() => handleUpdateUserLimit(user.id, Math.max(1, (user.max_instances || 3) - 1))}
                          disabled={!user.max_instances || user.max_instances <= 1}
                          title={t('enterprise.common.decreaseLimit')}
                        >
                          -
                        </button>
                        
                        {limitInputs[user.id] !== undefined ? (
                          <div className="limit-input-container">
                            <input
                              type="number"
                              className={`limit-input ${limitInputErrors[user.id] ? 'error' : ''}`}
                              value={limitInputs[user.id]}
                              onChange={(e) => handleLimitInputChange(user.id, e.target.value)}
                              onBlur={() => handleLimitInputBlur(user.id)}
                              onKeyPress={(e) => handleLimitInputKeyPress(e, user.id)}
                              min="1"
                              max={enterprise?.max_instances || 300}
                              title="Digite o número de instâncias"
                            />
                            {limitInputErrors[user.id] && (
                              <span className="limit-input-error">{limitInputErrors[user.id]}</span>
                            )}
                          </div>
                        ) : (
                          <span 
                            className="limit-display"
                            onClick={() => setLimitInputs(prev => ({ ...prev, [user.id]: user.max_instances || 3 }))}
                            title="Clique para editar"
                          >
                            {user.max_instances || 3}
                          </span>
                        )}
                        
                        <button
                          className="limit-btn increase"
                          onClick={() => {
                            const currentTotal = users.reduce((total, u) => total + (u.max_instances || 3), 0);
                            const availableLimit = (enterprise.max_instances || 300) - currentTotal + (user.max_instances || 3);
                            const newLimit = Math.min(availableLimit, (user.max_instances || 3) + 1);
                            handleUpdateUserLimit(user.id, newLimit);
                          }}
                          disabled={false}
                          title={t('enterprise.common.increaseLimit')}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="user-actions">
                    <button
                      className="user-details-btn"
                      onClick={() => handleShowUserDetails(user)}
                      title={t('enterprise.common.viewDetails')}
                    >
                      ⋯
                    </button>
                    <button
                      className="delete-user-btn"
                      onClick={() => handleDeleteUser(user.id)}
                      title={t('enterprise.common.removeUser')}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginação de usuários */}
            {totalUsersPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-controls">
                  <select 
                    value={usersPerPage} 
                    onChange={(e) => {
                      setUsersPerPage(Number(e.target.value));
                      setUsersPage(1);
                    }}
                    className="items-per-page-select"
                  >
                                  <option value={10}>10 {t('enterprise.pagination.perPage')}</option>
              <option value={25}>25 {t('enterprise.pagination.perPage')}</option>
              <option value={50}>50 {t('enterprise.pagination.perPage')}</option>
                  </select>
                  
                  <div className="pagination-buttons">
                    <button
                      className="pagination-btn"
                      onClick={() => setUsersPage(usersPage - 1)}
                      disabled={usersPage === 1}
                    >
                      {t('enterprise.pagination.previous')}
                    </button>
                    
                    <div className="pagination-numbers">
                      {Array.from({ length: Math.min(5, totalUsersPages) }, (_, i) => {
                        let pageNum;
                        if (totalUsersPages <= 5) {
                          pageNum = i + 1;
                        } else if (usersPage <= 3) {
                          pageNum = i + 1;
                        } else if (usersPage >= totalUsersPages - 2) {
                          pageNum = totalUsersPages - 4 + i;
                        } else {
                          pageNum = usersPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-number ${usersPage === pageNum ? 'active' : ''}`}
                            onClick={() => setUsersPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      className="pagination-btn"
                      onClick={() => setUsersPage(usersPage + 1)}
                      disabled={usersPage === totalUsersPages}
                    >
                      {t('enterprise.pagination.next')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

                {showCreateUser && (
          <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
            <div className="modal-content create-user-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-content">
                  <div className="modal-icon">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </div>
                  <div className="modal-title-section">
                    <h3>{t('enterprise.users.form.title')}</h3>
                    <p className="modal-subtitle">{t('enterprise.users.form.subtitle')}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="close-modal-btn"
                  onClick={() => setShowCreateUser(false)}
                  title={t('enterprise.users.form.close')}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="create-user-form">
                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserPlus} className="form-icon" />
                    {t('enterprise.users.form.name')}
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder={t('enterprise.users.form.namePlaceholder')}
                    required
                    autoComplete="name"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserPlus} className="form-icon" />
                    {t('enterprise.users.form.email')}
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder={t('enterprise.users.form.emailPlaceholder')}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserPlus} className="form-icon" />
                    {t('enterprise.users.form.phone')}
                  </label>
                  <input
                    type="tel"
                    value={newUser.phone_number}
                    onChange={(e) => setNewUser({...newUser, phone_number: e.target.value.replace(/\D/g, '').slice(0, 18)})}
                    placeholder={t('enterprise.users.form.phonePlaceholder')}
                    autoComplete="tel"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faUserPlus} className="form-icon" />
                    {t('enterprise.users.form.password')}
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder={t('enterprise.users.form.passwordPlaceholder')}
                    minLength={6}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowCreateUser(false)}
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    {t('enterprise.users.form.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="confirm-btn"
                    disabled={createUserLoading}
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    {createUserLoading ? t('enterprise.users.form.creating') : t('enterprise.users.form.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showUserDetails && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
            <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('enterprise.users.details.title')}</h3>
              <div className="user-details-info">
                <div className="detail-row">
                  <strong>{t('enterprise.users.details.name')}:</strong>
                  <span>{selectedUser.name}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('enterprise.users.details.email')}:</strong>
                  <span>{selectedUser.email}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('enterprise.users.details.role')}:</strong>
                  <span>{selectedUser.enterprise_role || 'User'}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('enterprise.users.details.memberSince')}:</strong>
                  <span>{new Date(selectedUser.created_at).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('enterprise.users.details.instanceLimit')}:</strong>
                  <span>{selectedUser.max_instances || 3}</span>
                </div>
              </div>
              
              <div className="user-instances">
                <h4>{t('enterprise.users.details.instances')} ({userInstances.length})</h4>
                <div className="instances-list">
                  {userInstances.map(instance => (
                    <div key={instance.id} className="instance-item">
                      <span className="instance-name">{instance.name || instance.instanceName}</span>
                      <span className="instance-status">{instance.status || t('enterprise.instances.unknown')}</span>
                      <span className="instance-date">{new Date(instance.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowUserDetails(false)}
                >
                  {t('enterprise.users.details.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {connectInstance && (
          <ConnectQrModal
            isOpen={connectModalOpen}
            onClose={handleCloseConnectModal}
            connection={connectInstance}
            navigate={null}
            isEnterprise={true}
          />
        )}

        <CreateInstanceModal
          isOpen={showCreateInstance}
          onClose={() => setShowCreateInstance(false)}
          currentUser={currentUser}
          enterprise={enterprise}
          onQRModal={(instance) => {
            // Abre o modal do QR code
            setConnectInstance({
              id: instance.id,
              name: instance.name,
              instance: instance.instanceName,
              instanceName: instance.instanceName,
              rawData: {
                server_id: instance.server_id
              }
            });
            setConnectModalOpen(true);
          }}
          onInstanceCreated={async (instanceData) => {
            // Recarrega as instâncias e dados da enterprise
            try {
              // Atualiza dados da enterprise
              const { data: enterpriseData } = await supabase
                .from('Enterprises')
                .select('*')
                .eq('id', id)
                .single();
              
              if (enterpriseData) {
                setEnterprise(enterpriseData);
              }

              // Busca usuários do enterprise
              const { data: allUsersData } = await supabase
                .from('Users')
                .select('id, name, email, enterprise_role, created_at, instances_total, max_instances')
                .eq('enterprise_id', id);

              const allUsers = allUsersData || [];
              
              // Determina quais instâncias carregar baseado no role
              let userIds = [];
              if (currentUser?.enterprise_role === 'Admin') {
                userIds = allUsers?.map(u => u.id) || [];
              } else {
                userIds = [currentUser?.id].filter(Boolean);
              }

              if (userIds.length > 0) {
                const { data: instancesData } = await supabase
                  .from('Instances')
                  .select(`
                    *,
                    Users(name, email, enterprise_role)
                  `)
                  .in('user_id', userIds);

                setInstances(instancesData || []);
              } else {
                setInstances([]);
              }

              // Se instanceData foi passado, abre o modal do QR
              if (instanceData) {
                setConnectInstance({
                  id: instanceData.id,
                  name: instanceData.name,
                  instance: instanceData.instanceName,
                  instanceName: instanceData.instanceName,
                  rawData: {
                    server_id: instanceData.server_id
                  }
                });
                setConnectModalOpen(true);
              }
            } catch (error) {
              console.error('Erro ao recarregar dados após criação:', error);
            }
          }}
        />

        {showRecreateConfirm && instanceToRecreate && (
          <div className="modal-overlay" onClick={() => setShowRecreateConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Confirmar Reinicialização</h3>
              <div style={{ marginBottom: '2rem' }}>
                <p style={{ color: '#e2e8f0', marginBottom: '1rem' }}>
                  Tem certeza que deseja reiniciar a instância?
                </p>
                <div style={{ 
                  background: 'rgba(245, 158, 11, 0.1)', 
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ color: '#fcd34d', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
                    Instância: {instanceToRecreate.name || instanceToRecreate.instanceName}
                  </p>
                  <p style={{ color: '#fcd34d', fontSize: '0.9rem', margin: 0 }}>
                    Esta ação irá reiniciar a instância e gerar um novo QR code.
                  </p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowRecreateConfirm(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={handleRecreateInstance}
                  disabled={recreateLoading}
                  style={{ background: '#f59e0b' }}
                >
                  {recreateLoading ? 'Reiniciando...' : 'Confirmar Reinicialização'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && instanceToDelete && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ color: '#ef4444' }}>{t('enterprise.deleteInstance.title')}</h3>
              <div style={{ marginBottom: '2rem' }}>
                <p style={{ color: '#e2e8f0', marginBottom: '1rem' }}>
                  {t('enterprise.deleteInstance.message', { instanceName: instanceToDelete.name || instanceToDelete.instanceName })}
                </p>
                <div style={{ 
                  background: 'rgba(239, 68, 68, 0.1)', 
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ color: '#fca5a5', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
                    {t('enterprise.deleteInstance.instanceName')}: {instanceToDelete.name || instanceToDelete.instanceName}
                  </p>
                  <p style={{ color: '#fca5a5', fontSize: '0.9rem', margin: 0 }}>
                    {t('enterprise.deleteInstance.permanentRemoval')}
                  </p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  {t('enterprise.deleteInstance.cancel')}
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={confirmDeleteInstance}
                  disabled={recreateLoading}
                  style={{ background: '#ef4444', color: 'white' }}
                >
                  {recreateLoading ? t('enterprise.deleteInstance.deleting') : t('enterprise.deleteInstance.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRecreateModal && instanceToRecreate && (
          <div className="modal-overlay" onClick={() => setShowRecreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>{t('enterprise.recreateInstance.title')}</h3>
              <div style={{ marginBottom: '2rem' }}>
                <p style={{ color: '#e2e8f0', marginBottom: '1rem' }}>
                  {t('enterprise.recreateInstance.message', { instanceName: instanceToRecreate.name || instanceToRecreate.instanceName })}
                </p>
                <div style={{ 
                  background: 'rgba(245, 158, 11, 0.1)', 
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ color: '#fcd34d', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
                    {t('enterprise.recreateInstance.instanceName')}: {instanceToRecreate.name || instanceToRecreate.instanceName}
                  </p>
                  <p style={{ color: '#fcd34d', fontSize: '0.9rem', margin: 0 }}>
                    {t('enterprise.recreateInstance.restartWarning')}
                  </p>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowRecreateModal(false)}
                >
                  {t('enterprise.recreateInstance.cancel')}
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={confirmRecreateInstance}
                  disabled={recreateLoading}
                  style={{ background: '#f59e0b' }}
                >
                  {recreateLoading ? t('enterprise.recreateInstance.recreating') : t('enterprise.recreateInstance.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Logout */}
        {showLogoutModal && (
          <div className="modal-overlay" onClick={() => setShowLogoutModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <FaTriangleExclamation style={{ fontSize: '3rem', color: '#f59e0b', marginBottom: '1rem' }} />
                <h3>{t('header.confirmLogout')}</h3>
                <p style={{ color: '#e2e8f0', marginBottom: '1rem' }}>
                  {t('header.confirmLogoutMsg')}
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowLogoutModal(false)}
                >
                  {t('header.cancel')}
                </button>
                <button
                  type="button"
                  className="confirm-btn"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  style={{ background: '#f59e0b' }}
                >
                  {loggingOut ? t('header.loggingOut') : t('header.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Enterprise; 